import React, { useState, useRef, useEffect } from 'react'
import { Icon } from '@/components/Icon'

interface ConversationSearchProps {
  onSearch: (value: string) => void
  onNewConversation: () => void
}

const ConversationSearch: React.FC<ConversationSearchProps> = ({
  onSearch,
  onNewConversation,
}) => {
  const [showSearch, setShowSearch] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const searchRef = useRef<HTMLDivElement>(null)

  // 自动聚焦搜索输入框
  useEffect(() => {
    if (showSearch) {
      // 延迟聚焦，确保DOM元素已经渲染
      const timer = setTimeout(() => {
        const input = searchRef.current?.querySelector('input')
        if (input) {
          input.focus()
        }
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [showSearch])

  // 处理点击外部关闭搜索
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setShowSearch(false)
        setSearchValue('')
        onSearch('')
      }
    }

    if (showSearch) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showSearch, onSearch])

  // 处理搜索按钮点击
  const handleSearchClick = () => {
    setShowSearch(true)
  }

  // 处理搜索输入框变化
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchValue(value)
    onSearch(value)
  }

  // 处理搜索框失焦
  const handleSearchBlur = () => {
    // 延迟收起，给用户一点时间点击搜索结果
    setTimeout(() => {
      setShowSearch(false)
      setSearchValue('')
      onSearch('')
    }, 200)
  }

  return (
    <div className="relative h-16">
      {/* 开启新对话按钮 */}
      <button
        onClick={() => onNewConversation()}
        className={`z-20 absolute left-4 top-4 flex items-center justify-center gap-3 px-4 py-3 text-left rounded-xl transition-all duration-100 ${
          showSearch ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
        style={{
          backgroundColor: 'rgba(236, 227, 255, 0.54)',
          color: '#3C19E9',
          fontSize: '16px',
          position: 'relative',
          width: 'calc(100% - 90px)', // 减去右边距和搜索按钮宽度
          height: '48px',
        }}
      >
        <Icon name="chat" className="text-[#3C19E9] w-6 h-6" />
        <span className="font-medium">开启新对话</span>
        {/* 小箭头 */}
        <div
          className="absolute left-1/4 transform -translate-x-1/2"
          style={{
            width: '0',
            height: '0',
            bottom: '-10px',
            borderRight: '16px solid transparent',
            borderLeft: '0px solid transparent',
            borderTop: '10px solid rgba(236, 227, 255, 0.54)',
          }}
        />
      </button>

      {/* 搜索按钮 */}
      <button
        onClick={handleSearchClick}
        title="搜索对话"
        className={`z-20 absolute right-4 top-4 flex items-center justify-center rounded-xl transition-all duration-100 ${
          showSearch ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
        style={{
          backgroundColor: 'rgba(236, 227, 255, 0.54)',
          color: '#3C19E9',
          width: '48px',
          height: '48px',
        }}
      >
        <Icon
          name="search"
          className="text-[#3C19E9] w-5 h-5 -translate-x-0.5 -translate-y-0.5"
        />
      </button>

      {/* 搜索输入框 */}
      <div className="absolute top-4 w-full px-4 z-10">
        <div
          ref={searchRef}
          className={`transition-all duration-300 ${
            showSearch ? 'w-full opacity-100' : 'w-0 opacity-0 overflow-hidden'
          }`}
          style={{
            height: '48px',
            marginLeft: 'auto', // 让元素靠右对齐
          }}
        >
          <input
            type="text"
            value={searchValue}
            onChange={handleSearchChange}
            onBlur={handleSearchBlur}
            placeholder="搜索对话..."
            className="w-full h-full px-4 py-3 text-base rounded-xl border-none outline-none focus:ring-0"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              color: '#3C19E9',
              border: '1px solid rgba(60, 25, 233, 0.2)',
              boxShadow: '0 2px 8px rgba(60, 25, 233, 0.1)',
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default ConversationSearch
